import { Widget, AdminWidget, BenchmarkOption, ApexChartType } from '@/interfaces/Dashboard';
import AvailableIcons from '@/interfaces/AvailableIcons';

const defaultColors = [
  'var(--connx-primary)',
  'var(--connx-success)',
  'var(--connx-warning)',
  'var(--connx-danger)',
  '#775DD0',
  'var(--connx-secondary)',
  '#26A69A',
  '#D10CE8',
];

/**
 * Returns a new color array with specific indexes highlighted using provided highlight colors
 * @param indexesToHighlight - array of indexes to highlight
 * @param highlightColors - array of colors to use for highlighting, matches indexes array
 * @param baseColors - optional base color array, defaults to defaultColors
 */
const getHighlightedColors = (
  indexesToHighlight: number[],
  highlightColors: string[],
  baseColors = defaultColors
): string[] => {
  if (indexesToHighlight.length !== highlightColors.length) {
    throw new Error('indexesToHighlight and highlightColors must have the same length');
  }

  // Copy base colors
  const colorsCopy = [...baseColors];

  // Remove any highlight colors from base to avoid duplicates
  const colorsWithoutHighlights = colorsCopy.filter((c) => !highlightColors.includes(c));

  // Build the new color array
  const newColors: string[] = [];
  let j = 0; // index for colorsWithoutHighlights

  for (let i = 0; i < colorsCopy.length; i++) {
    const highlightIndex = indexesToHighlight.indexOf(i);
    if (highlightIndex !== -1) {
      newColors.push(highlightColors[highlightIndex]);
    } else {
      newColors.push(colorsWithoutHighlights[j]);
      j++;
    }
  }

  return newColors;
};

const chartTypeMap: Record<string, ApexChartType> = {
  line: 'line',
  area: 'area',
  bar: 'bar',
  column: 'bar',
  pie: 'pie',
  donut: 'donut',
  radialbar: 'radialBar',
  scatter: 'scatter',
  bubble: 'bubble',
  heatmap: 'heatmap',
  candlestick: 'candlestick',
  boxplot: 'boxPlot',
  radar: 'radar',
  polararea: 'polarArea',
  rangebar: 'rangeBar',
  rangearea: 'rangeArea',
  treemap: 'treemap',
};

const normalizeVisualType = (type: string): string => {
  return type.replace(/[^a-z]/gi, '').toLowerCase();
};

const normalizeChartType = (type: string): ApexChartType => {
  if (!type) return 'bar';
  const key = type.replace(/[^a-z]/gi, '').toLowerCase();

  return chartTypeMap[key] ?? 'bar';
};

interface IconManagement {
  icon: AvailableIcons;
  class?: string;
  label: string;
}

const chartIconMap = new Map<string, IconManagement>([
  ['table', { icon: 'fas fa-table', label: 'Table' }],
  ['bar', { icon: 'fas fa-chart-bar', class: 'tw-transform -tw-rotate-90 tw-scale-y-[-1]', label: 'Bar' }],
  ['column', { icon: 'fas fa-signal-alt', label: 'Column' }],
  ['line', { icon: 'fas fa-chart-line', label: 'Line' }],
  ['area', { icon: 'fas fa-wave-square', label: 'Area' }],
  ['pie', { icon: 'fas fa-chart-pie', label: 'Pie' }],
  ['donut', { icon: 'fas fa-circle-notch', label: 'Donut' }],
  ['radialbar', { icon: 'fas fa-bullseye', label: 'Radial Bar' }],
  ['scatter', { icon: 'fas fa-braille', label: 'Scatter' }],
  ['bubble', { icon: 'fas fa-circle', label: 'Bubble' }],
  ['heatmap', { icon: 'fas fa-th', label: 'Heatmap' }],
  ['candlestick', { icon: 'fas fa-sliders-h', label: 'Candle Stick' }],
  ['boxplot', { icon: 'fas fa-align-center', label: 'Boxplot' }],
  ['radar', { icon: 'fas fa-sitemap', label: 'Radar' }],
  ['polararea', { icon: 'fas fa-crosshairs', label: 'Polar Area' }],
  ['rangebar', { icon: 'fas fa-sliders-h', label: 'Range Bar' }],
  ['rangearea', { icon: 'fas fa-wave-square', label: 'Range Area' }],
  ['treemap', { icon: 'fas fa-th-large', label: 'Tree Map' }],
  ['title', { icon: 'fas fa-heading', label: 'Title' }],
  // Custom Display Types
  ['advancedcolumnchart', { icon: 'fas fa-signal-alt', label: 'Advanced Column Chart' }],
  [
    'advancedcolumnchartstacked',
    { icon: 'fas fa-align-left', class: 'tw-transform -tw-rotate-90 ', label: 'Advanced Column Chart (Stacked)' },
  ],
  [
    'advancedcolumnchartstacked100',
    { icon: 'fas fa-tasks-alt', class: 'tw-transform -tw-rotate-90 ', label: 'Advanced Column Chart (100% Stacked)' },
  ],
]);

function getWidgetVisualIcon<T extends Widget | AdminWidget>(widget: T): IconManagement {
  const currentDisplayType = (
    'properties' in widget
      ? widget.defaultJsonProperties.currentDisplayType
      : widget.defaultJsonProperties.currentDisplayType
  )?.toLowerCase();

  const customDisplayType = (
    'componentType' in widget ? widget.defaultJsonProperties.componentType : widget.defaultVisualType
  )?.toLowerCase();

  const customDisplayIcon = (customDisplayType && chartIconMap.get(customDisplayType)) || null;
  const defaultDisplayIcon = currentDisplayType ? chartIconMap.get(currentDisplayType) || null : null;

  if (customDisplayIcon) return customDisplayIcon;
  if (defaultDisplayIcon) return defaultDisplayIcon;

  return { icon: 'fas fa-analytics', label: 'Chart' };
}

const buildColorRanges = (benchmarks: BenchmarkOption, minValue = 0, maxValue = Number.MAX_SAFE_INTEGER) => {
  const { values, levels, expectation } = benchmarks;

  let sorted: number[];
  if (expectation === 'low') {
    // Low is good → smallest threshold first
    sorted = [...values].sort((a, b) => a - b);
  } else {
    // High is good → largest threshold first
    sorted = [...values].sort((a, b) => b - a);
  }

  // Add boundaries
  const points = expectation === 'low' ? [minValue, ...sorted, maxValue] : [maxValue, ...sorted, minValue];

  return levels.map((level, i) => {
    let from = points[i];
    let to = points[i + 1];

    // Ensure from < to for rendering (swap if needed)
    if (from > to) [from, to] = [to, from];

    return { from, to, color: level.color };
  });
};

export { defaultColors, normalizeChartType, getHighlightedColors, chartIconMap, getWidgetVisualIcon, buildColorRanges };
